import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { clientKeys } from '../queries/keys';
import { clientPaths } from '../queries/paths';

// TSS vite/03 — server-state hooks for clients. Keys + fetchers + invalidation
// only; cache/dedup/retry is TanStack Query's job. Mutations are PATCH/POST/DELETE
// per api-reference/admin-clients.md.

export function useClientsList(filters) {
    return useQuery({
        queryKey: clientKeys.list(filters),
        queryFn:  ({ signal }) => api.get(clientPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev, // keep previous page while refetching on filter change
    });
}

export function useClient(id) {
    return useQuery({
        queryKey: clientKeys.detail(id),
        queryFn:  ({ signal }) => api.get(clientPaths.detail(id), { signal }).then(r => r.data),
        enabled:  !!id && id !== 'new',
    });
}

// GET /admin/clients/:id/holdings — listas de donadoras (co-posesión activa) y receptoras
// (fincas propias ∪ tag parcería) del cliente (admin-client-holdings.md · 0105). Read-only.
export function useClientHoldings(id) {
    return useQuery({
        queryKey: clientKeys.holdings(id),
        queryFn:  ({ signal }) => api.get(clientPaths.holdings(id), { signal }).then(r => r.data),
        enabled:  !!id && id !== 'new',
    });
}

export function useCreateClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(clientPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: clientKeys.all }),
    });
}

export function useUpdateClient(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(clientPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: clientKeys.detail(id) });
            qc.invalidateQueries({ queryKey: clientKeys.all });
        },
    });
}

// POST /admin/clients/:id/portal-invite — genera el enlace de registro/reset del cliente
// (admin-clients.md · 0046). Habilita el LOGIN del cliente: consume el link en el portal y pone
// su contraseña → se crea/enlaza su usuario. purpose auto (register si sin cuenta; reset si ya
// tiene). Devuelve el token en claro UNA vez + el link. 429 si supera 3 regeneraciones/24h.
export function useCreatePortalInvite(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.post(clientPaths.portalInvite(id), {}).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: clientKeys.detail(id) }),
    });
}

// POST /admin/clients/:id/link-user — crea un usuario NUEVO (role owner) y lo vincula a un
// cliente huérfano (admin-clients.md · 877bc3c). `password` opcional (≥10): con clave → login
// inmediato sin invite; sin clave → emite invite de reset. Devuelve { user, invite|password_set }.
export function useLinkUser(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(clientPaths.linkUser(id), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: clientKeys.detail(id) }),
    });
}

// POST /admin/clients/:id/set-password — (emergencia) fija la clave de un cliente que YA tiene
// usuario; bumpea token_version → revoca sus sesiones. Body { password } (≥10).
export function useSetClientPassword(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (password) => api.post(clientPaths.setPassword(id), { password }).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: clientKeys.detail(id) }),
    });
}

export function useDeleteClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(clientPaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: clientKeys.all }),
    });
}
